// ODION site sections (od-*): booking links that carry ad UTMs, the Tempo buy box,
// the Elevate "complete the suit" picker, the fit finder and the pinned phone buy bar.
// Talks to the theme only through Horizon's cart event, like ob-builder.js.
import { CartAddEvent } from '@theme/events';

const money = (cents) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: cents % 100 ? 2 : 0 });

/* ---------- Booking links ---------- */
// Ads land with ?utm_*; keep the first set we see this visit so a click two pages later still credits the ad.
const TRACK_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid'];
const STORE_KEY = 'od_utms';

function readUtms() {
  const here = new URLSearchParams(location.search);
  const fresh = {};
  for (const k of TRACK_KEYS) if (here.get(k)) fresh[k] = here.get(k);
  try {
    if (Object.keys(fresh).length) {
      fresh.landing_page = location.pathname;
      sessionStorage.setItem(STORE_KEY, JSON.stringify(fresh));
      return fresh;
    }
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
  } catch {
    return fresh;
  }
}

function decorateBookLinks(root = document) {
  const utms = readUtms();
  root.querySelectorAll('a[data-od-book], a[href^="/apps/help/book"]').forEach((a) => {
    const url = new URL(a.getAttribute('href'), location.origin);
    for (const [k, v] of Object.entries(utms)) if (!url.searchParams.has(k)) url.searchParams.set(k, v);
    a.href = url.pathname + url.search;
  });
}

/* ---------- Add to cart (shared) ---------- */
async function addItems(el, items) {
  const sections = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sections.join(',') }),
  });
  const json = await res.json();
  if (!res.ok || json.status) throw new Error(json.description || json.message || 'We could not add that to your cart.');
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  el.dispatchEvent(
    new CartAddEvent({}, String(items[0].id), {
      source: el.localName,
      itemCount: cart.item_count,
      variantId: String(items[0].id),
      sections: json.sections,
    })
  );
  return cart;
}

const readJson = (el, sel) => {
  try {
    return JSON.parse(el.querySelector(sel)?.textContent || 'null');
  } catch {
    return null;
  }
};

/* ---------- Pinned buy bar on phones ---------- */
function pinBar(el, target) {
  const pin = el.querySelector('.od-pin');
  if (!pin || !target || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(([e]) => pin.classList.toggle('is-on', !e.isIntersecting));
  io.observe(target);
}

/* ---------- Tempo buy box ---------- */
const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
const SWATCHES = { white: '#fff', blue: '#9fb8d6' };

class OdTempoBuy extends HTMLElement {
  connectedCallback() {
    this.data = readJson(this, 'script[data-products]');
    if (!this.data) return;
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-status]');
    this.addBtn = this.querySelector('[data-add]');
    this.priceEl = this.querySelector('[data-price]');
    this.img = this.querySelector('[data-variant-image]');
    this.form.addEventListener('change', (e) => {
      if (e.target.name === 'fit') this.renderOptions(true);
      this.update();
    });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.add();
    });
    this.querySelectorAll('[data-pin-add]').forEach((b) => b.addEventListener('click', () => this.add()));
    this.renderOptions(false);
    this.update();
    pinBar(this, this.addBtn);
  }

  get fit() {
    return this.form.elements.fit?.value || Object.keys(this.data)[0];
  }

  // Tempo+ Slim and Classic are a combined listing, so each product also carries a "Fit" option.
  // The Fit switch picks the product; these are the other choices (color, size, sleeve).
  opts(product = this.data[this.fit]) {
    return product.options.map((o, i) => ({ ...o, i })).filter((o) => !/^fit$/i.test(o.name));
  }

  pick(i) {
    return this.form.elements[`o${i}`]?.value;
  }

  find(product, override = {}) {
    const opts = this.opts(product);
    return product.variants.find((v) => opts.every((o) => v.options[o.i] === (o.i in override ? override[o.i] : this.pick(o.i))));
  }

  defaultFor(product, name) {
    const n = name.toLowerCase();
    if (n.includes('color')) return product.defaults.color;
    if (n.includes('sleeve')) return product.defaults.sleeve;
    return product.defaults.size;
  }

  // Build the pills for the chosen fit, keeping the shopper's picks when they still exist.
  renderOptions(keep) {
    const product = this.data[this.fit];
    this.opts(product).forEach((opt, row) => {
      const el = this.querySelector(`[data-opt="${row}"]`);
      if (!el) return;
      // Only values this fit is actually made in, in size order.
      let values = opt.values.filter((v) => product.variants.some((x) => x.options[opt.i] === v));
      if (/size/i.test(opt.name)) values = values.sort((a, b) => SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b));
      const prev = keep ? this.pick(opt.i) : null;
      const def = this.defaultFor(product, opt.name);
      const want = prev && values.includes(prev) ? prev : values.includes(def) ? def : values[0];
      el.dataset.optIndex = opt.i;
      el.querySelector('[data-opt-name]').textContent = opt.name;
      el.querySelector('.od-opt__row').innerHTML = values
        .map((v) => {
          const sw = /color/i.test(opt.name) ? `<i style="--sw:${SWATCHES[v.toLowerCase()] || '#ccc'}"></i>` : '';
          return `<label class="od-pill${sw ? ' od-swatch' : ''}"><input type="radio" name="o${opt.i}" value="${v}"${v === want ? ' checked' : ''}><span>${sw}${v}</span></label>`;
        })
        .join('');
    });
  }

  get variant() {
    return this.find(this.data[this.fit]);
  }

  update() {
    const product = this.data[this.fit];
    // Grey out picks that are sold out given the other choices.
    this.opts(product).forEach((opt, row) => {
      this.form.querySelectorAll(`input[name="o${opt.i}"]`).forEach((input) => {
        const v = this.find(product, { [opt.i]: input.value });
        input.disabled = !v || !v.available;
      });
      const label = this.querySelector(`[data-opt="${row}"] [data-opt-value]`);
      if (label) label.textContent = this.pick(opt.i) || '';
    });
    const v = this.variant;
    const ok = v && v.available;
    this.addBtn.disabled = !ok;
    this.addBtn.querySelector('[data-add-label]').textContent = !v ? 'Pick a size' : ok ? `Add to cart · ${money(v.price)}` : 'Sold out in this size';
    if (v) this.priceEl.textContent = money(v.price);
    if (v?.image && this.img) {
      this.img.src = v.image;
      this.img.removeAttribute('srcset');
    }
    const pinLabel = this.querySelector('[data-pin-label]');
    if (pinLabel && v) pinLabel.textContent = `${product.label} · ${this.opts(product).map((o) => v.options[o.i]).join(' / ')}`;
    this.querySelectorAll('[data-pin-add]').forEach((b) => (b.disabled = !ok));
  }

  async add() {
    const v = this.variant;
    if (!v || !v.available) return;
    this.addBtn.disabled = true;
    this.addBtn.querySelector('[data-add-label]').textContent = 'Adding…';
    this.status.textContent = '';
    this.status.classList.remove('od-buy__status--error');
    try {
      await addItems(this, [{ id: v.id, quantity: 1 }]);
      this.status.textContent = 'Added. Your cart is up top.';
      this.querySelector('[data-after]')?.removeAttribute('hidden');
    } catch (e) {
      this.status.textContent = `${e.message} Try another size.`;
      this.status.classList.add('od-buy__status--error');
    }
    this.update();
  }
}

/* ---------- Elevate: coat + nested slack in one tap ---------- */
// Nested slack = Slim Fit pant, same color, waist = coat chest minus the drop (6": a 42R coat gets a 36 waist).
class OdSuitBuy extends HTMLElement {
  connectedCallback() {
    this.data = readJson(this, 'script[data-suit]');
    if (!this.data) return;
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-status]');
    this.addBtn = this.querySelector('[data-add]');
    this.custom = this.querySelector('[data-custom]');
    this.querySelector('[data-toggle-custom]')?.addEventListener('click', () => {
      this.custom.hidden = !this.custom.hidden;
      this.querySelector('[data-toggle-custom]').setAttribute('aria-expanded', String(!this.custom.hidden));
      if (this.custom.hidden) this.form.elements.pant_mode.value = 'nested';
      else this.form.elements.pant_mode.value = 'custom';
      this.update();
    });
    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.add();
    });
    this.querySelectorAll('[data-pin-add]').forEach((b) => b.addEventListener('click', () => this.add()));
    this.applyFinder();
    this.update();
    pinBar(this, this.addBtn);
  }

  // If the shopper used the fit finder, start on that size.
  applyFinder() {
    let fit = null;
    try {
      fit = JSON.parse(localStorage.getItem('od_fit') || 'null');
    } catch {}
    if (!fit) return;
    const set = (name, val) => {
      const input = this.form.querySelector(`input[name="${name}"][value="${val}"]`);
      if (input) input.checked = true;
    };
    set('size', fit.chest);
    set('coat_length', fit.length);
    if (fit.waist && fit.waist !== Number(fit.chest) - this.data.drop) {
      this.custom.hidden = false;
      this.form.elements.pant_mode.value = 'custom';
      // Use the closest waist we make at or above the suggestion.
      const sizes = [...this.form.elements.pant_waist.options].map((o) => Number(o.value)).sort((a, b) => a - b);
      this.form.elements.pant_waist.value = String(sizes.find((s) => s >= Number(fit.waist)) ?? sizes.at(-1));
    }
    const note = this.querySelector('[data-fit-note]');
    if (note) {
      note.textContent = `From your fit finder: ${fit.chest}${fit.length} coat, ${fit.waist} waist.`;
      note.hidden = false;
    }
    this.fitAnswers = fit;
  }

  get coat() {
    const size = this.form.elements.size?.value;
    const length = this.form.elements.coat_length?.value;
    return this.data.coats.find((c) => c.size === size && c.length === length);
  }

  get pant() {
    const mode = this.form.elements.pant_mode.value;
    const coat = this.coat;
    if (mode === 'none') return null;
    let fit = this.data.default_fit || 'classic';
    let waist = coat ? String(Number(coat.size) - this.data.drop) : null;
    if (mode === 'custom') {
      fit = this.form.elements.pant_fit.value;
      waist = this.form.elements.pant_waist.value;
    }
    return this.data.pants[fit]?.find((p) => p.size === waist) || { missing: true, fit, waist };
  }

  update() {
    const coat = this.coat;
    const pant = this.pant;
    const mode = this.form.elements.pant_mode.value;
    // Sold-out sizes and lengths
    this.form.querySelectorAll('input[name="size"]').forEach((i) => {
      const len = this.form.elements.coat_length?.value;
      i.disabled = !this.data.coats.some((c) => c.size === i.value && (!len || c.length === len) && c.available);
    });
    this.form.querySelectorAll('input[name="coat_length"]').forEach((i) => {
      const size = this.form.elements.size?.value;
      i.disabled = !this.data.coats.some((c) => (!size || c.size === size) && c.length === i.value && c.available);
    });
    ['size', 'coat_length'].forEach((n) => {
      const out = this.querySelector(`[data-chosen="${n}"]`);
      if (out) out.textContent = this.form.elements[n]?.value || '';
    });

    const nestedText = this.querySelector('[data-nested]');
    const nestedSub = this.querySelector('[data-nested-sub]');
    const fitName = this.data.default_fit === 'slim' ? 'Slim' : 'Classic';
    if (nestedText) {
      nestedText.textContent = coat
        ? `${fitName} fit · ${Number(coat.size) - this.data.drop} waist · ${this.data.color}`
        : `${fitName} fit · ${this.data.color}`;
    }
    if (nestedSub) {
      nestedSub.textContent = coat
        ? `$${this.data.pant_price / 100} · the usual match for a ${coat.size}${coat.length} coat (a 6" drop). It goes in your cart with the coat.`
        : `$${this.data.pant_price / 100} · pick your coat size and we match the waist (a 42 coat gets a 36).`;
    }

    const pantOk = mode === 'none' || (pant && !pant.missing && pant.available);
    const coatOk = coat && coat.available;
    const total = (coat ? coat.price : this.data.coat_price) + (mode === 'none' ? 0 : pant && !pant.missing ? pant.price : this.data.pant_price);
    this.querySelector('[data-total]').textContent = money(total);

    let label = `Add suit to cart · ${money(total)}`;
    if (!coat) label = this.form.elements.size?.value ? 'Pick your length' : 'Pick your coat size';
    else if (!coatOk) label = 'Coat sold out in this size';
    else if (!pantOk) label = pant?.missing ? `No ${pant.waist} waist in ${this.data.color}` : 'Pant sold out in this waist';
    else if (mode === 'none') label = `Add coat to cart · ${money(total)}`;
    this.addBtn.disabled = !(coatOk && pantOk);
    this.addBtn.querySelector('[data-add-label]').textContent = label;
    const pinLabel = this.querySelector('[data-pin-label]');
    if (pinLabel) pinLabel.textContent = coat ? `${coat.size}${coat.length} coat${mode === 'none' ? '' : ` + ${pant?.waist || ''} pant`}` : 'Elevate Suit';
    this.querySelectorAll('[data-pin-add]').forEach((b) => (b.disabled = this.addBtn.disabled));
  }

  async add() {
    const coat = this.coat;
    const pant = this.pant;
    if (!coat) return;
    const suitTag = `${this.data.color} Elevate ${coat.size}${coat.length}`;
    const items = [{ id: coat.id, quantity: 1, properties: { _suit: suitTag } }];
    if (pant && !pant.missing) items.push({ id: pant.id, quantity: 1, properties: { _suit: suitTag } });
    this.addBtn.disabled = true;
    this.addBtn.querySelector('[data-add-label]').textContent = 'Adding…';
    this.status.textContent = '';
    this.status.classList.remove('od-buy__status--error');
    try {
      await addItems(this, items);
      // Keep the fit finder answers on the order so sizes can be checked against exchanges later.
      if (this.fitAnswers) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { 'Fit finder': this.fitAnswers.summary } }),
        }).catch(() => {});
      }
      this.status.textContent = 'Added. Your cart is up top.';
    } catch (e) {
      this.status.textContent = `${e.message} Try another size.`;
      this.status.classList.add('od-buy__status--error');
    }
    this.update();
  }
}

/* ---------- Fit finder ---------- */
// Rule-based start from the Elevate size chart. Answers are kept in localStorage so the suit picker
// starts on the suggested size, and they ride along on the order as a cart attribute.
// Elevate runs a little long, so each band sits an inch taller than the usual chart:
// someone on the edge of Long wears Regular, and on the edge of Regular wears Short.
const LENGTHS = [
  { max: 68, code: 'S' }, // up to 5'8"
  { max: 72, code: 'R' }, // 5'9"–6'0"
  { max: 76, code: 'L' }, // 6'1"–6'4"
  { max: 999, code: 'XL' }, // 6'5" and up
];
const CHEST_BY_WEIGHT = [
  [135, 36], [150, 38], [165, 40], [185, 42], [205, 44], [225, 46], [245, 48], [265, 50], [285, 52], [9999, 54],
];

export function recommend({ feet, inches, weight, jacket, build, jean }) {
  const height = Number(feet) * 12 + Number(inches || 0);
  const length = LENGTHS.find((l) => height <= l.max).code;
  const byWeight = CHEST_BY_WEIGHT.find(([w]) => Number(weight) < w)[1];
  let chest = byWeight;
  if (build === 'athletic') chest += 2;
  let confidence = 'medium';
  if (jacket) {
    const j = Math.round(Number(jacket) / 2) * 2;
    confidence = Math.abs(j - chest) <= 2 ? 'high' : 'low';
    chest = j;
  }
  chest = Math.min(54, Math.max(36, chest));
  // The nested slack is the coat size minus 6 (42 coat → 36 waist). That's the normal match, not a rule.
  const nested = chest - 6;
  let waist = nested;
  let waistFrom = 'nested';
  if (jean) {
    // Dress slacks fit at least one size up from jeans: a 34 jean usually means a 36 slack.
    waist = Number(jean) + 2;
    waistFrom = 'jean';
    // Elevate slacks come in every size up to 34, then even sizes only.
    if (waist > 34 && waist % 2) waist += 1;
  } else if (build === 'athletic') {
    waist = chest - 8;
    waistFrom = 'build';
  } else if (build === 'fuller') {
    waist = chest - 4;
    waistFrom = 'build';
  }
  return { chest: String(chest), length, waist, nested, waistFrom, confidence, height };
}

class OdFitFinder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.result = this.querySelector('[data-result]');
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.show();
    });
  }

  show() {
    const f = this.form.elements;
    const answers = { feet: f.feet.value, inches: f.inches.value, weight: f.weight.value, jacket: f.jacket.value, build: f.build.value, jean: f.jean?.value };
    const error = this.querySelector('[data-error]');
    if (!answers.feet || !answers.weight) {
      if (error) error.hidden = false;
      (answers.feet ? f.weight : f.feet).focus();
      return;
    }
    if (error) error.hidden = true;
    const r = recommend(answers);
    const notes = {
      high: 'High confidence. Your usual jacket size lines up with your height and weight.',
      medium: 'Good starting point. Most guys your height and weight wear this. If you know your usual jacket size, add it for a closer match.',
      low: 'Your usual jacket size and your height and weight point to different sizes. We went with your usual size, but a quick fitting will settle it.',
    };
    this.result.querySelector('[data-size]').textContent = `${r.chest}${r.length} coat · ${r.waist} waist`;
    let pant;
    if (r.waist === r.nested) {
      pant = `Pant: ${r.waist} waist, the standard nested slack for a ${r.chest} coat (6" drop).`;
    } else if (r.waistFrom === 'jean') {
      pant = `Pant: ${r.waist} waist, one size up from your ${answers.jean} jean, which is how dress slacks usually fit. The standard match for a ${r.chest} coat is ${r.nested}, so we'll set your waist to ${r.waist} instead.`;
    } else {
      pant = `Pant: ${r.waist} waist (${answers.build === 'athletic' ? 'athletic build' : 'fuller through the middle'}). The standard match for a ${r.chest} coat is ${r.nested}; pick ${r.waist} under “Pick a different waist or fit”.`;
    }
    this.result.querySelector('[data-pant]').textContent = pant;
    this.result.querySelector('[data-conf]').textContent = notes[r.confidence];
    this.result.hidden = false;
    const summary = `${answers.feet}'${answers.inches || 0}" ${answers.weight}lb jacket:${answers.jacket || '-'} jean:${answers.jean || '-'} build:${answers.build} -> ${r.chest}${r.length}/${r.waist} (${r.confidence})`;
    try {
      localStorage.setItem('od_fit', JSON.stringify({ ...r, waist: r.waist, summary }));
    } catch {}
    document.querySelectorAll('od-suit-buy').forEach((s) => {
      s.applyFinder?.();
      s.update?.();
    });
    this.result.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

if (!customElements.get('od-tempo-buy')) customElements.define('od-tempo-buy', OdTempoBuy);
if (!customElements.get('od-suit-buy')) customElements.define('od-suit-buy', OdSuitBuy);
if (!customElements.get('od-fit-finder')) customElements.define('od-fit-finder', OdFitFinder);

decorateBookLinks();
document.addEventListener('shopify:section:load', (e) => decorateBookLinks(e.target));
